import React, { useEffect, useState } from 'react';
import { useRealKmClient } from '../../lib/useRealKmClient.js';
import RisposteSelfAssessmentModal from './RisposteSelfAssessmentModal.jsx';

/* ═══════════════════════════════════════════════════════════
   Esperienze di un consulente — richiesto il 24/09: dalla scheda
   Consulenti, un consulente può avere più esperienze, quindi cliccare il
   nome apre PRIMA questo elenco (non direttamente le risposte, che
   richiedono una singola esperienza scelta).

   Usa GET /esperienze?consulente_id=X — stesso endpoint già usato dal
   link "aperte/totali" esistente, qui solo con vista diversa.

   Profilo SA derivato dallo stato (non restituito esplicitamente
   dall'endpoint): 'aperta' → SA-2b (ciclo trimestrale, timer sulla
   risorsa), altrimenti → SA-4 (chiusura). Coerente con la matrice
   stato→profilo dell'AF v1.3 §7.1 — R3b riceve SA-2b mentre l'esperienza
   resta attiva, SA-4 scatta alla chiusura.

   Ordinamento: la risposta non include data_apertura, solo
   data_chiusura_prevista — usata come proxy per "più recente", non è
   la stessa cosa ma è il solo campo data disponibile qui.
═══════════════════════════════════════════════════════════ */

const T = {
  border: 'rgba(0,0,0,0.12)',
  surface: '#FFFFFF',
  surface2: '#F4F3EF',
  txt: '#1A1916',
  txt2: '#6B6A65',
  txt3: '#A3A19C',
  accent: '#185FA5',
  redBg: '#FCEBEB',
  redTxt: '#791F1F',
  greenBg: '#EAF3DE',
  greenTxt: '#27500A',
};

// 15/10 — SAL 02/10 azione 23: nessun codice SA-xx a schermo. Nome leggibile
// del questionario previsto per l'esperienza (aperta: racconto periodico,
// SA-2b; chiusa: questionario di chiusura, SA-4).
function profiloSA(stato) {
  return stato === 'aperta'
    ? { label: 'Racconto periodico', desc: 'ogni 3 mesi' }
    : { label: 'Chiusura dell’esperienza', desc: 'a fine progetto' };
}

// Stato dell'esperienza come nella lista esperienze (non il valore tecnico).
const STATO_ESPERIENZA = { aperta: 'In corso', in_chiusura: 'In chiusura', chiusa: 'Chiusa' };

export default function EsperienzeConsulenteModal({ consulenteId, nome, onClose }) {
  const kmClient = useRealKmClient();
  const [status, setStatus] = useState('loading');
  const [esperienze, setEsperienze] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [risposteEsperienzaId, setRisposteEsperienzaId] = useState(null);

  useEffect(() => {
    if (!consulenteId) return;
    setStatus('loading');
    kmClient
      .get('/esperienze', { query: { consulente_id: consulenteId } })
      .then((data) => {
        const lista = Array.isArray(data?.esperienze) ? [...data.esperienze] : [];
        lista.sort((a, b) => new Date(b.data_chiusura_prevista || 0) - new Date(a.data_chiusura_prevista || 0));
        setEsperienze(lista);
        setStatus('ready');
      })
      .catch((err) => {
        setErrorMessage(err?.detail || 'Errore nel caricamento delle esperienze.');
        setStatus('error');
      });
  }, [kmClient, consulenteId]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: T.surface, borderRadius: 14, width: '100%', maxWidth: 640, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.txt }}>Esperienze</div>
            <div style={{ fontSize: 12.5, color: T.txt2 }}>{nome || consulenteId}</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.txt2, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          {status === 'loading' && <div style={{ color: T.txt3, textAlign: 'center', padding: 20 }}>Caricamento…</div>}
          {status === 'error' && <div style={{ padding: 12, borderRadius: 8, background: T.redBg, color: T.redTxt, fontSize: 13 }}>{errorMessage}</div>}
          {status === 'ready' && esperienze.length === 0 && (
            <div style={{ color: T.txt3, textAlign: 'center', padding: 20 }}>Nessuna esperienza per questo consulente.</div>
          )}

          {status === 'ready' && esperienze.map((e) => {
            const profilo = profiloSA(e.stato);
            return (
              <div
                key={e.esperienza_id}
                style={{ padding: 12, marginBottom: 8, borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface2 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: T.txt }}>{e.cliente || 'Cliente non specificato'}</div>
                  <span
                    className="badge"
                    style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, ...(e.stato === 'aperta' ? { background: T.greenBg, color: T.greenTxt } : { background: T.border, color: T.txt2 }) }}
                  >
                    {STATO_ESPERIENZA[e.stato] || e.stato}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: T.txt2, marginTop: 2 }}>{e.ruolo || '—'} · {e.periodo || '—'}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <span style={{ fontSize: 11.5, color: T.txt2 }}>
                    Questionario previsto: <strong style={{ color: T.txt }}>{profilo.label}</strong> ({profilo.desc})
                  </span>
                  <button type="button" className="btn-link" style={{ fontSize: 12 }} onClick={() => setRisposteEsperienzaId(e.esperienza_id)}>
                    Vedi risposte grezze →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {risposteEsperienzaId && (
        <RisposteSelfAssessmentModal
          resourceId={risposteEsperienzaId}
          nome={nome}
          tipo="esperienza"
          onClose={() => setRisposteEsperienzaId(null)}
        />
      )}
    </div>
  );
}
